/**
 * Persisted download-directory handle for Approach C folder downloads.
 *
 * Chrome/Edge only. The user picks a destination folder ONCE; the directory
 * handle is stored in IndexedDB and re-used silently on every later download
 * (Chrome persists the permission when the user chooses "Allow on every
 * visit"; otherwise a lightweight Allow confirmation — never the folder
 * picker again).
 */

const DB_NAME = 'amm-download-dir';
const STORE = 'handles';
const KEY = 'download-dir';

export function isFsaSupported(): boolean {
  return typeof (window as any).showDirectoryPicker === 'function';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function loadPersistedDirHandle(): Promise<any | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const get = tx.objectStore(STORE).get(KEY);
      get.onsuccess = () => resolve(get.result || null);
      get.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function persistDirHandle(handle: any): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(handle, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* best effort */
  }
}

/**
 * Returns a directory handle with readwrite permission.
 * - Stored handle + persisted permission → fully silent.
 * - Stored handle, permission 'prompt' → one lightweight Allow dialog.
 * - No stored handle (first ever use) → the one-time folder picker.
 * Must be called within a user gesture (the download click).
 * Throws the picker's AbortError if the user dismisses it.
 */
export async function ensureDownloadDirectory(): Promise<any> {
  const stored = await loadPersistedDirHandle();
  if (stored) {
    try {
      let permission = await stored.queryPermission({ mode: 'readwrite' });
      if (permission === 'prompt') {
        permission = await stored.requestPermission({ mode: 'readwrite' });
      }
      if (permission === 'granted') {
        return stored;
      }
    } catch {
      /* stale handle (folder deleted/moved) — fall through to picker */
    }
  }

  const picked = await (window as any).showDirectoryPicker({
    mode: 'readwrite',
    startIn: 'downloads',
    id: 'amm-folder-downloads',
  });
  await persistDirHandle(picked);
  return picked;
}
